import { useEffect, useState, useMemo } from "react";
import { API_URL } from "../config";
import { useAssignKeywords } from "../hooks/useAssignKeywords";
import { useSelection } from "../hooks/useSelection";
import FiltersLabelPrompt from "../components/filters/FiltersLabelPrompts";
import ImageTableView from "../components/ImageTableView";
import Pagination from "../components/Pagination";
import Notification from "../components/Notification";

function AssignKeywords() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  const { selected, toggle, toggleAll, clear } = useSelection();
  const [refreshKey, setRefreshKey] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [keywordsInput, setKeywordsInput] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load labels
  useEffect(() => {
    fetch(`${API_URL}/api/status/labels_keywords`)
      .then((res) => res.json())
      .then((data) => setAvailableLabels(data.map((l) => ({ value: l.id, label: l.name }))))
      .catch((err) => console.error("Error loading labels:", err));
  }, []);

  // Load prompts
  useEffect(() => {
    const url = new URL(`${API_URL}/api/approve/prompts`);
    if (selectedLabel) url.searchParams.append("labels", selectedLabel.label);
    url.searchParams.append("status", "preapproved");
    fetch(url.toString())
      .then((res) => res.json())
      .then((data) =>
        setAvailablePrompts(
          data.map((p) => ({
            value: p.id,
            label: p.content.length > 170 ? p.content.slice(0, 170) + "..." : p.content,
          }))
        )
      )
      .catch((err) => console.error("Error loading prompts:", err));
  }, [selectedLabel]);

  const filters = useMemo(() => ({
    labels: selectedLabel ? [selectedLabel.value] : [],
    prompts: selectedPrompt ? [selectedPrompt.value] : [],
  }), [selectedLabel, selectedPrompt]);

  const { images, imagesCount, loading } = useAssignKeywords({ page, limit, filters, refreshKey });

  const handleAssign = async () => {
    try {
      setLoadingSubmit(true);
      const res = await fetch(`${API_URL}/api/assign_keywords/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected, keywords: keywordsInput }),
      });
      if (!res.ok) throw new Error("Request failed");

      clear();
      setKeywordsInput("");
      setShowModal(false);
      setRefreshKey((prev) => prev + 1);
      setNotification({ message: "Keywords assigned successfully", type: "success" });
    } catch (err) {
      setNotification({ message: `Error: ${err.message}`, type: "error" });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div>
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <FiltersLabelPrompt
        availableLabels={availableLabels}
        selectedLabel={selectedLabel}
        setSelectedLabel={(v) => { setSelectedLabel(v); setPage(1); clear(); }}
        availablePrompts={availablePrompts}
        selectedPrompt={selectedPrompt}
        setSelectedPrompt={(v) => { setSelectedPrompt(v); setPage(1); clear(); }}
      />

      <select className="form-select mb-3" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); clear(); }}>
        <option value={100}>100</option>
        <option value={500}>500</option>
        <option value={1000}>1000</option>
        <option value={10000}>10000</option>
      </select>

      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-primary" disabled={selected.length === 0} onClick={() => setShowModal(true)}>
          Add Keywords
        </button>
      </div>

      {showModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Keywords</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. bed, night, room"
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleAssign}
                  disabled={loadingSubmit || !keywordsInput.trim()}
                >
                  {loadingSubmit ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className="row align-items-end">
          <h2 className="col-3">Assign Keywords</h2>
          <div className="col-9" />
        </div>
      </div>

      <Pagination
        page={page}
        totalCount={imagesCount}
        limit={limit}
        loading={loading}
        onPrev={() => { setPage(page - 1); clear(); }}
        onNext={() => { setPage(page + 1); clear(); }}
      />

      <ImageTableView
        images={images}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
      />
    </div>
  );
}

export default AssignKeywords;
